import React, {useCallback, useEffect, useState} from 'react';
import {Button, Checkbox, DatePicker, Empty, Input, Modal, Space, Spin, Tag, message} from 'antd';
import dayjs from 'dayjs';
import {FileText, Info, PlayCircle, Power, Printer, ReceiptText, RefreshCw, Send, Type, UnlockKeyhole, XCircle} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {APIv1} from '../../../api';
import {extractDateBySecond} from '../../../utils';
import './DeviceOperations.scss';
import {playFeedbackWave} from "../../../utils/feedbackWave";

const DEVICE_OPERATION_OPTIONS = [
    {
        labelKey: 'openZReport',
        value: 'open_zreport',
        Icon: UnlockKeyhole,
        tone: 'green',
    },
    {
        labelKey: 'closeZReport',
        value: 'close_zreport',
        Icon: XCircle,
        tone: 'red',
    },
    {
        labelKey: 'xReport',
        value: 'print_xreport',
        Icon: FileText,
        tone: 'blue',
    },
    {
        labelKey: 'reboot',
        value: 'reboot',
        Icon: Power,
        tone: 'purple',
    },
    {
        labelKey: 'printLastOrder',
        value: 'print_last_order',
        Icon: ReceiptText,
        tone: 'orange',
    },
    {
        labelKey: 'orderSendReceipt',
        value: 'order_send_receipt',
        Icon: Send,
        tone: 'cyan',
    },
    {
        labelKey: 'printInfo',
        value: 'print_info',
        Icon: Info,
        tone: 'slate',
    },
    {
        labelKey: 'sendLastLog',
        value: 'send_last_log',
        Icon: Printer,
        tone: 'pink',
    },
    {
        labelKey: 'printCustomText',
        value: 'print_custom_text',
        Icon: Type,
        tone: 'slate',
    },
];

const DATE_FORMAT = 'YYYY-MM-DD';
const SEND_LAST_LOG_OPERATION = 'send_last_log';
const PRINT_CUSTOM_TEXT_OPERATION = 'print_custom_text';

const DeviceOperations = ({serialNumber, token, iconOnly = false, buttonClassName, onTriggerClick}) => {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOperations, setSelectedOperations] = useState([]);
    const [logDate, setLogDate] = useState(() => dayjs());
    const [customText, setCustomText] = useState('');
    const [isForCompany, setIsForCompany] = useState(false);
    const [deviceOperations, setDeviceOperations] = useState([]);
    const [operationsLoading, setOperationsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const operationOptions = DEVICE_OPERATION_OPTIONS.map(option => ({
        label: t(`pages.devices.operations.items.${option.labelKey}`),
        value: option.value,
        Icon: option.Icon,
        tone: option.tone,
    }));

    const operationLabels = operationOptions.reduce((labels, option) => ({
        ...labels,
        [option.value]: option.label,
    }), {});
    const hasSendLastLogOperation = selectedOperations.includes(SEND_LAST_LOG_OPERATION);
    const hasPrintCustomTextOperation = selectedOperations.includes(PRINT_CUSTOM_TEXT_OPERATION);

    const getStatusColor = (status) => {
        if (status === 'success') return 'green';
        if (status === 'pending') return 'gold';
        if (status === 'failed' || status === 'error') return 'red';

        return 'default';
    };

    const getValidationErrorMessage = (error) => {
        if (error?.response?.status !== 400) return null;

        const responseData = error.response.data;

        if (Array.isArray(responseData)) {
            return responseData.filter(Boolean).join(' ');
        }

        if (typeof responseData === 'string') {
            return responseData;
        }

        if (responseData && typeof responseData === 'object') {
            const messages = Object.values(responseData).flat().filter(Boolean);
            return messages.join(' ');
        }

        return null;
    };

    const fetchDeviceOperations = useCallback(async () => {
        if (!serialNumber || !token) return;

        setOperationsLoading(true);

        try {
            const response = await APIv1.get('/device/operations/', {
                params: {
                    dsn: serialNumber,
                },
                headers: {
                    Authorization: `Token ${token}`,
                },
            });

            setDeviceOperations(response.data.operations ?? []);
        } catch (error) {
            console.error('Failed to fetch device operations:', error);
            message.error(t('pages.devices.operations.messages.fetchError'));
        } finally {
            setOperationsLoading(false);
        }
    }, [serialNumber, token, t]);

    useEffect(() => {
        if (isOpen) {
            fetchDeviceOperations();
        }
    }, [isOpen, fetchDeviceOperations]);

    const closeModal = () => {
        if (submitting) return;

        setIsOpen(false);
    };

    const toggleOperation = (operationName) => {
        setSelectedOperations((currentOperations) => (
            currentOperations.includes(operationName)
                ? currentOperations.filter(selectedOperation => selectedOperation !== operationName)
                : [...currentOperations, operationName]
        ));
    };

    const clearSelectedOperations = () => {
        setSelectedOperations([]);
        setLogDate(dayjs());
        setCustomText('');
        setIsForCompany(false);
    };

    const handleSubmit = async () => {
        if (!selectedOperations.length) {
            message.warning(t('pages.devices.operations.messages.selectRequired'));
            return;
        }

        if (hasPrintCustomTextOperation && !customText.trim()) {
            message.warning(t('pages.devices.operations.messages.customTextRequired'));
            return;
        }

        setSubmitting(true);

        try {
            await APIv1.post('/device/operations/create/', {
                device_serial_number: serialNumber,
                operations: selectedOperations.map(operationName => {
                    if (operationName === PRINT_CUSTOM_TEXT_OPERATION) {
                        return {
                            operation_name: operationName,
                            custom_text: customText.trim(),
                            is_for_company: isForCompany,
                        };
                    }

                    return {
                        operation_name: operationName,
                        log_date: operationName === SEND_LAST_LOG_OPERATION && logDate
                            ? logDate.format(DATE_FORMAT)
                            : null,
                    };
                }),
            }, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });

            playFeedbackWave('success');
            message.success(t('pages.devices.operations.messages.success'));
            setSelectedOperations([]);
            setLogDate(dayjs());
            setCustomText('');
            setIsForCompany(false);
            await fetchDeviceOperations();
        } catch (error) {
            console.error('Failed to create device operations:', error);
            const validationMessage = getValidationErrorMessage(error);
            playFeedbackWave('failure');
            message.error(
                validationMessage
                    ? t('pages.devices.operations.messages.validationError', {message: validationMessage})
                    : t('pages.devices.operations.messages.error')
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button
                type="button"
                className={buttonClassName ?? 'detail-view__action-button detail-view__action-button--primary'}
                onClick={(event) => {
                    onTriggerClick?.(event);
                    setIsOpen(true);
                }}
                aria-label={t('pages.devices.operations.button')}
                title={t('pages.devices.operations.button')}
            >
                <PlayCircle size={16}/>
                {!iconOnly && <span>{t('pages.devices.operations.button')}</span>}
            </button>

            <Modal
                title={t('pages.devices.operations.title')}
                open={isOpen}
                onCancel={closeModal}
                footer={null}
                destroyOnClose
                width={980}
            >
                <div className="device-operations">
                    <div className="device-operations__toolbar">
                        <div className="device-operations__target">
                            <span>{t('pages.devices.operations.deviceLabel')}</span>
                            <Tag color="blue">{serialNumber}</Tag>
                        </div>

                        <Space size={8} wrap>
                            <Button onClick={closeModal} disabled={submitting}>
                                {t('pages.devices.operations.cancelButton')}
                            </Button>
                            <Button
                                type="primary"
                                loading={submitting}
                                disabled={!selectedOperations.length}
                                onClick={handleSubmit}
                            >
                                {t('pages.devices.operations.sendButton')}
                            </Button>
                        </Space>
                    </div>

                    <div className="device-operations__content">
                        <div className="device-operations__picker">
                            <div className="device-operations__cards">
                                {operationOptions.map(({label, value, Icon, tone}) => {
                                    const isSelected = selectedOperations.includes(value);

                                    return (
                                        <button
                                            type="button"
                                            key={value}
                                            className={`device-operations__card device-operations__card--${tone}${isSelected ? ' device-operations__card--selected' : ''}`}
                                            onClick={() => toggleOperation(value)}
                                            aria-pressed={isSelected}
                                        >
                                            <span className="device-operations__card-icon">
                                                <Icon size={20}/>
                                            </span>
                                            <span className="device-operations__card-title">{label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                        </div>

                        <div className="device-operations__list">
                            <div className="device-operations__list-header">
                                <div className="device-operations__list-title">
                                    {t('pages.devices.operations.listTitle')}
                                </div>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<RefreshCw size={15}/>}
                                    loading={operationsLoading}
                                    onClick={fetchDeviceOperations}
                                    aria-label={t('pages.devices.operations.reloadButton')}
                                    title={t('pages.devices.operations.reloadButton')}
                                />
                            </div>

                            {operationsLoading ? (
                                <div className="device-operations__loading">
                                    <Spin/>
                                </div>
                            ) : deviceOperations.length > 0 ? (
                                <div className="device-operations__table">
                                    <div className="device-operations__table-head">
                                        <span>{t('pages.devices.operations.columns.operationId')}</span>
                                        <span>{t('pages.devices.operations.columns.name')}</span>
                                        <span>{t('pages.devices.operations.columns.status')}</span>
                                        <span>{t('pages.devices.operations.columns.createdDate')}</span>
                                    </div>

                                    {deviceOperations.map(operation => (
                                        <div className="device-operations__table-row" key={operation.operation_id}>
                                            <span>{operation.operation_id}</span>
                                            <span>{operationLabels[operation.operation_name] || operation.operation_name}</span>
                                            <span>
                                                <Tag color={getStatusColor(operation.status)}>
                                                    {operation.status}
                                                </Tag>
                                            </span>
                                            <span>{extractDateBySecond(operation.created_date)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t('pages.devices.operations.emptyList')}
                                />
                            )}

                            {selectedOperations.length > 0 && (
                                <div className="device-operations__order">
                                    <div className="device-operations__order-header">
                                        <span>{t('pages.devices.operations.executionOrder')}</span>
                                        <Button
                                            type="link"
                                            size="small"
                                            danger
                                            onClick={clearSelectedOperations}
                                            disabled={submitting}
                                        >
                                            {t('pages.devices.operations.clearAllButton')}
                                        </Button>
                                    </div>
                                    <Space size={[6, 6]} wrap>
                                        {selectedOperations.map((operationName, index) => (
                                            <Tag key={operationName} color={index === 0 ? 'green' : 'default'}>
                                                {index + 1}. {operationLabels[operationName] || operationName}
                                            </Tag>
                                        ))}
                                    </Space>

                                    {hasSendLastLogOperation && (
                                        <div className="device-operations__date-field">
                                            <span>{t('pages.devices.operations.logDateLabel')}</span>
                                            <DatePicker
                                                allowClear
                                                value={logDate}
                                                format={DATE_FORMAT}
                                                onChange={setLogDate}
                                                className="device-operations__date-picker"
                                            />
                                        </div>
                                    )}

                                    {hasPrintCustomTextOperation && (
                                        <div className="device-operations__text-field">
                                            <span>{t('pages.devices.operations.customTextLabel')}</span>
                                            <Checkbox
                                                className="device-operations__text-field__switcher"
                                                checked={isForCompany}
                                                onChange={(event) => setIsForCompany(event.target.checked)}
                                            >
                                                {t('pages.devices.operations.isForCompanyLabel')}
                                            </Checkbox>
                                            <Input.TextArea
                                                className="device-operations__text-field__text-area"
                                                value={customText}
                                                onChange={(event) => setCustomText(event.target.value)}
                                                placeholder={t('pages.devices.operations.customTextPlaceholder')}
                                                autoSize={{minRows: 3, maxRows: 5}}
                                                maxLength={500}
                                                showCount
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default DeviceOperations;
